<template>
  <transition name="ad">
    <div v-show="visible" class="ad-page" :style="{ '--ad-fade-ms': fadeMs + 'ms' }">
      <img
        v-if="currentImage"
        :src="currentImage"
        alt="广告页"
        class="ad-image"
        decoding="async"
        fetchpriority="high"
      />
      <button v-if="showSkip" class="skip-button" @click="finishAd">
        {{ $t('adpage.skip') }} {{ countdown }} 
      </button>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useRouter,useRoute } from 'vue-router';
import { useUserStore } from 'd3components';
import { adService } from './AdService';

const router = useRouter();
const route = useRoute();
const userStore = useUserStore();// 状态
const startupAd = adService.consume();
// 作为全局覆盖层：默认隐藏，仅在有广告数据时显示
const visible = ref(!!startupAd); // 有广告数据后立即显示，避免先露出登录页
const showSkip = ref(!!startupAd); // 是否显示“跳过”按钮
const countdown = ref(startupAd?.duration ? Math.ceil(startupAd.duration / 1000) : 5); // 倒计时
//  初始化 提供广告图片
const currentImage = ref<string | null>(startupAd?.imageUrl ?? null); 
// 淡入/淡出同步；来自广告数据的 fadeDuration
const fadeMs = ref(startupAd?.fadeDuration ?? 500); // 控制淡入/淡出速度

let timer: number | null = null; 

// 停止计时
const stopTimer = () => {
  if (timer) {
    // 清除计时器
    clearInterval(timer);
    timer = null;
  }
};

// 预加载图片，确保加载完成后再显示
const preloadImage = (url: string, timeoutMs = 1500) => {
  return new Promise<void>((resolve, reject) => {
    let done = false;
    const img = new Image();
    const cleanup = () => {
      img.onload = null;
      img.onerror = null;
    };
    // 设置超时
    const to = window.setTimeout(() => {
      if (!done) {
        done = true;
        cleanup();
        reject(new Error('图片加载超时'));
      }
    }, timeoutMs);
    // 设置图片加载成功
    img.onload = () => {
      if (!done) {
        done = true;
        cleanup();
        window.clearTimeout(to);
        resolve();
      }
    };
    // 设置图片加载失败
    img.onerror = (e) => {
      if (!done) {
        done = true;
        cleanup();
        window.clearTimeout(to);
        reject(e as any);
      }
    };
    img.src = url;
  });
};

// 广告结束或点击跳过后的处理
const finishAd = async () => {
  //  停止计时 ，标记首次启动完成 ，重置广告状态
  stopTimer();
  adService.reset();
  adService.markFirstLaunch?.();
  adService.setStartupBlocking(false);
  // 先导航到目标页（下一页会开始在覆盖层之下渲染），以实现交叉淡出
  const isLoggedIn = !!(userStore?.auth && Object.keys(userStore.auth).length);
  if (!isLoggedIn) {
    router.replace({ path: '/login',query:route.query }).catch(() => {});
  } else {
    try {
      // 确保菜单与动态路由准备就绪
      if (!userStore.routerData || userStore.routerData.length === 0) {
        try { await userStore.firstMenuFun?.(); } catch {}
      }
      if (userStore.routerData && userStore.routerData.length > 0) {
        try {
          const { addDynamicRoutes } = await import('@/router');
          await addDynamicRoutes();
        } catch {}
        const firstMenu: any = userStore.routerData[0];
        const subs = Array.isArray(firstMenu?.subs) ? firstMenu.subs : [];
        const firstChildUri = subs.length > 0 ? subs[0]?.uri : undefined;
        const firstChildPid = subs.length > 0 ? subs[0]?.pid : undefined;
        if (firstChildUri) {
          router.replace(firstChildUri).catch(() => {});
        } else if (firstChildPid) {
          router.replace(`/page/${firstChildPid}`).catch(() => {});
        } else if (firstMenu?.pid) {
          router.replace(`/page/${firstMenu.pid}`).catch(() => {});
        } else {
          router.replace('/').catch(() => {});
        }
      } else {
        router.replace('/').catch(() => {});
      }
    } catch {
      router.replace('/').catch(() => {});
    }
  }
  // 触发覆盖层淡出（下一页已在下层渲染，用户可在淡出过程中看到目标页）
  window.setTimeout(() => {
    visible.value = false; // 触发离场动画
  }, 0);
};

// 倒计时逻辑：显示“跳过”，每秒减 1，到 0 自动执行 finishAd
const startCountdown = (seconds = 5) => {
  if (seconds <= 0) {
    finishAd();
    return;
  }
  // 设置倒计时
  countdown.value = seconds;
  // 显示跳过按钮
  showSkip.value = true;
  // 设置计时器
  timer = window.setInterval(() => {
    countdown.value -= 1; // 倒计时减1
    // 如果倒计时结束，关闭广告页
    if (countdown.value <= 0) {
      finishAd();
    }
  }, 1000);
};

// 生命周期
onMounted(async () => {



  console.log(route,'router.currentRoute.value.query')
  // 读取广告数据
  const ad = startupAd;
  console.log('ad',ad)
  // 设置广告图片
  // ad 存在且有 imageUrl 时，才设置图片
  if (!ad) {
    // 无广告时立即隐藏覆盖层
    visible.value = false;
    adService.setStartupBlocking(false);
    return;
  }
  // 立即设置图片与显示覆盖层，避免在预加载期间露出登录页
  if (ad.imageUrl) {
    currentImage.value = ad.imageUrl;
    // 在后台进行预加载，不阻塞显示
    preloadImage(ad.imageUrl, 1500).catch(() => {
      // 忽略预加载失败，继续显示
    });
  }
  // 同步淡入淡出时长
  fadeMs.value = 500;// ad?.fadeDuration ?? 1000;
  // 等一帧再显示覆盖层，确保从 opacity 0 过渡到广告页
  requestAnimationFrame(() => {
    visible.value = true;
  });
  // 倒计时
  startCountdown(ad?.duration ? ad.duration / 1000 : 5);
});

onBeforeUnmount(stopTimer); // 卸载时停止计时
</script>

<style scoped>
.ad-page {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;
  z-index: 9999;
}

.ad-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.skip-button {
  position: absolute;
  top: 5%;
  right: 4%;
  padding: 10px 32px;
  border: none;
  border-radius: 32px;
  background: rgba(0, 0, 0, 0.2);
  /* color: #fff; */
  /* background: var(--color-body); */
  color: #f1f1f1;
  font-size: 24px;
  font-weight: 400;
}

/* 让整个覆盖层淡入淡出，实现与下一页的交叉淡出 */
.ad-enter-active,
.ad-leave-active {
  transition: opacity var(--ad-fade-ms, 1000ms) ease;
}
.ad-enter-from,
.ad-leave-to {
  opacity: 0;
}
</style>
